import { getAuth } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { toast } from "react-toastify";
import remarkGfm from "remark-gfm";
import { useLanguage } from "../../context/Language/LanguageContext";
import {
    callAI,
    kAIProviderStorage,
    kProviders,
    kVolleyballScoringPrimer,
    keyStorageFor,
    modelStorageFor,
    providerConfig,
} from "../utils/aiProviders";
import { getRotationLabels } from "../utils/Constants";
import { sendShareNotification } from "../utils/emailNotify";
import {
    buildDeepAnalysisPrompt,
    buildMatchEventsCSV,
    downloadCSV,
    matchCSVFileName,
} from "../utils/MatchCSV";
import { buildReportPdf, canvasToJpeg, pdfToBase64 } from "../utils/reportPdf";
import { saveReportPdf, shareReport } from "../utils/teamPlayerData";
import { playerDocId } from "../utils/teamsPlayers";
import FollowUpQuestions from "./FollowUpQuestions";
import Share from "./Share";

// Maps the app's current language code to the English language name used when
// instructing the AI which language to write its report in.
const kLangCodeToAIName = {
  en: "English",
  no: "Norwegian",
  de: "German",
  fr: "French",
  nl: "Dutch",
  it: "Italian",
  es: "Spanish",
  ko: "Korean",
  ja: "Japanese",
  zh: "Chinese (Simplified)",
  vi: "Vietnamese",
  ru: "Russian",
  pt: "Portuguese",
  pl: "Polish",
  th: "Thai",
  tr: "Turkish",
};

const kLanguages = [
  "English",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Dutch",
  "Polish",
  "Russian",
  "Turkish",
  "Arabic",
  "Chinese (Simplified)",
  "Japanese",
  "Korean",
  "Vietnamese",
  "Thai",
];

// Flag emoji for each translate-target language (keyed by the English language
// name used in kLanguages). Emoji flags render reliably inside <option>
// elements, where <img>/SVG icons do not.
const kLangNameToFlag = {
  English: "🇬🇧",
  Spanish: "🇪🇸",
  French: "🇫🇷",
  German: "🇩🇪",
  Italian: "🇮🇹",
  Portuguese: "🇧🇷",
  Dutch: "🇳🇱",
  Polish: "🇵🇱",
  Russian: "🇷🇺",
  Turkish: "🇹🇷",
  Arabic: "🇸🇦",
  "Chinese (Simplified)": "🇨🇳",
  Japanese: "🇯🇵",
  Korean: "🇰🇷",
  Vietnamese: "🇻🇳",
  Thai: "🇹🇭",
};

const zoneLabel = (z) => `Zone ${z}`;

// Returns a privacy-safe label for the scouted player using only their jersey
// number, e.g. "Player #12". `data.name` has the form "12. NickName".
const playerJerseyLabel = (name) => {
  if (!name) {
    return "the player";
  }
  const num = String(name).split(".")[0].trim();
  return num ? `Player #${num}` : "the player";
};

// Re-labels setter tendencies as anonymous "Setter 1", "Setter 2", ... so that
// no setter names are sent to the AI provider.
const anonymizeSetters = (items) => {
  if (!items) {
    return items;
  }
  return items.map((t, idx) => ({ ...t, label: `Setter ${idx + 1}` }));
};

function buildPrompt(data, playerTeamLabel, opponentTeamLabel) {
  const jersey = playerJerseyLabel(data.name);
  const lines = [];
  lines.push(
    `You are scouting an attacker (referred to only by jersey number) on ${playerTeamLabel} ` +
      `so that ${opponentTeamLabel} can defend against ${playerTeamLabel}.`
  );
  lines.push("");
  lines.push(`Player: ${jersey} (${playerTeamLabel})`);
  lines.push(
    `Total attacks: ${data.totalSpikes} | Kills: ${data.kills} | Errors: ${data.errors} | In-play: ${data.inPlay}`
  );
  lines.push(
    `Hitting %: ${data.killPct.toFixed(1)}% kill | Error rate: ${(
      data.errorRate || 0
    ).toFixed(1)}% | Efficiency: ${data.efficiency.toFixed(1)}%`
  );
  if (data.attacksPerSet !== undefined && data.setCount) {
    lines.push(
      `Attack frequency: ${data.attacksPerSet.toFixed(1)} attacks per set over ${
        data.setCount
      } set(s)`
    );
  }
  lines.push("");
  if (data.startZones.length > 0) {
    lines.push("Attack origin (where the player hits from):");
    data.startZones.forEach((z) => {
      lines.push(
        `  ${zoneLabel(z.zone)}: ${z.total} attacks, ${z.kills} kills, ${
          z.errors
        } errors, ${z.killPct.toFixed(0)}% kill rate`
      );
    });
    lines.push("");
  }
  if (data.landingZones.length > 0) {
    lines.push("Landing zones (where the ball is hit to):");
    data.landingZones.forEach((z) => {
      lines.push(
        `  ${zoneLabel(z.zone)}: ${z.total} attacks landed, ${z.kills} kills`
      );
    });
    lines.push("");
  }

  const pushTendencies = (title, items) => {
    if (!items || items.length === 0) {
      return;
    }
    lines.push(title);
    items.forEach((t) => {
      lines.push(
        `  ${t.label}: ${t.total} attacks, ${t.kills} kills, ${
          t.errors
        } errors, ${t.killPct.toFixed(0)}% kill, ${t.efficiency.toFixed(
          0
        )}% eff`
      );
    });
    lines.push("");
  };

  pushTendencies(
    "First-ball sideout vs transition tendencies:",
    data.phaseTendencies
  );
  pushTendencies("Efficiency by rotation:", data.rotationTendencies);
  pushTendencies(
    "Front row vs back row attacks:",
    data.frontBackTendencies
  );
  pushTendencies(
    "Tendencies by set type and tempo:",
    data.setTypeTendencies
  );
  pushTendencies(
    "In-system vs out-of-system tendencies:",
    data.systemTendencies
  );
  pushTendencies(
    "High-leverage points (late & close score):",
    data.highLeverageTendencies
  );
  pushTendencies("Tendencies by score state:", data.scoreStateTendencies);
  pushTendencies(
    "Tendencies against number of blockers:",
    data.blockerTendencies
  );
  pushTendencies("Tendencies by setter:", anonymizeSetters(data.setterTendencies));
  pushTendencies(
    "Tendencies by reception / serve-receive pass grade:",
    data.passGradeTendencies
  );

  // Serve-receive (passing) profile of the player. Role data is rarely
  // reliable in imported rosters (most formats only ever flag the Libero),
  // so whether this player is a genuine serve-receive target is judged
  // mainly from how much they actually passed, not a labelled position.
  const passing = data.passing;
  const isLibero = data.role === "L";
  if (passing && passing.total > 0 && !passing.isLikelySecondaryReceiver) {
    lines.push("Serve-receive (passing) profile:");
    lines.push(
      `  This player IS a regular serve receiver${isLibero ? " (Libero)" : ""}: ${passing.total} receptions` +
        (passing.passesPerSet
          ? ` (${passing.passesPerSet.toFixed(1)} per set)`
          : "") +
        `, ${passing.positivePct.toFixed(0)}% positive, ${passing.perfectPct.toFixed(
          0
        )}% perfect, ${passing.errorPct.toFixed(0)}% reception errors`
    );
    if (passing.gradeTendencies && passing.gradeTendencies.length > 0) {
      lines.push("  Reception grade distribution:");
      passing.gradeTendencies.forEach((t) => {
        lines.push(`    ${t.label}: ${t.total} receptions`);
      });
    }
    if (passing.locations && passing.locations.length > 0) {
      lines.push("  Reception locations (court zone where they receive):");
      passing.locations.forEach((z) => {
        lines.push(`    ${zoneLabel(z.zone)}: ${z.total} receptions`);
      });
    }
    lines.push("");
  } else if (passing && passing.total > 0) {
    // A handful of receptions across the whole match is typical of a
    // non-passing role (middle blocker, opposite) occasionally catching a
    // stray/short serve — not a real serve-receive pattern worth targeting.
    lines.push(
      `Serve-receive: only ${passing.total} reception(s) recorded all match` +
        (passing.passesPerSet ? ` (${passing.passesPerSet.toFixed(1)} per set)` : "") +
        " — too small a sample to be a genuine serve-receive target. This player is likely in a non-passing role " +
        "(e.g. middle blocker or opposite) who is kept out of serve-receive rotations. Do NOT recommend serving at " +
        "them based on this handful of receptions."
    );
    lines.push("");
  } else {
    lines.push(
      "Serve-receive: this player has no recorded receptions in this data — they are not used as a serve receiver. " +
        "Do NOT recommend serving at them for serve-receive disruption."
    );
    lines.push("");
  }

  pushTendencies(
    "Attack efficiency when passing vs not passing (does receiving serve affect their attack?):",
    data.attackWhenPassingTendencies
  );

  lines.push(
    "Based on this data, provide a concise scouting report with three clearly labelled sections:"
  );
  lines.push("1. STRENGTHS — what this attacker does well.");
  lines.push("2. WEAKNESSES — where they are vulnerable or inefficient.");
  lines.push(
    "3. DEFENSIVE GAME PLAN — concrete blocking and defensive positioning recommendations to neutralise this player."
  );
  lines.push("");
  lines.push(
    "Where the data supports it, explicitly address the player's: attack frequency, hitting percentage / kill rate / error rate, efficiency by rotation, front row vs back row efficiency, tendencies by set type and tempo, tendencies by attack zone and target zone, in-system vs out-of-system tendencies, serve-receive pass-quality tendencies, first-ball sideout and transition tendencies, behaviour at high-leverage points, tendencies by score state, tendencies against 1/2/3 blockers, and tendencies by setter. If a category has little or no data, do not speculate about it."
  );
  lines.push(
    "Also assess the player's serve-receive role: whether they are a regular serve receiver (passer), how reliable their passing is (positive%, perfect%, error%), where on the court they receive, and whether their attacking efficiency differs when they have passed the serve versus when they have not. Only recommend serving at this player (to disrupt a weak pass or a worse post-pass attack) when they have a genuine, regular serve-receive sample — NOT when they only have a handful of receptions all match, which usually means a non-passing position (e.g. middle blocker or opposite) that is deliberately kept out of serve-receive. If they have no receptions, or only a trivial handful, explicitly state they should not be targeted with serve for reception disruption."
  );
  lines.push(
    "Keep it practical and specific to volleyball court zones. Use short bullet points."
  );
  lines.push(
    "When referring to a rotation, always use the exact rotation label shown in the data (e.g. \"Rotation " +
      (getRotationLabels()[0] || "R1") +
      "\"). Never refer to a rotation by a bare number such as \"Rotation 1\"."
  );
  lines.push(
    "Format the report using Markdown: use ## headings for each section, **bold** for key numbers and player tendencies, *italics* for emphasis, and bullet lists. Do not wrap the whole response in a code block."
  );
  return lines.join("\n");
}

function AICoachPanel({ players, selectedPlayerGuid, onPlayerSelected, analysisData, isPlayerTeamA = true, matches, selectedGame, selectedTeam = 0 }) {
  const { t, lang, allLanguages } = useLanguage();
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-4o-mini");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [translateTo, setTranslateTo] = useState("");
  const [translateNonce, setTranslateNonce] = useState(0);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [savingReport, setSavingReport] = useState(false);
  const [sharingReport, setSharingReport] = useState(null);
  const [shareNonce, setShareNonce] = useState(0);
  const [showSettings, setShowSettings] = useState(true);
  // "player" = single-attacker scouting; "team" = full ball-by-ball deep
  // analysis of the selected team from the event CSV.
  const [mode, setMode] = useState("player");
  const reportRef = useRef(null);

  const canTeamAnalyse = Boolean(matches && matches.length > 0);
  const selectedTeamLabel = selectedTeam === 0 ? "Team A" : "Team B";

  // The report is always generated in the app's currently selected language.
  const language =
    kLangCodeToAIName[lang] || allLanguages[lang]?.label || "English";

  // The AI settings are considered configured once a provider, API key and
  // model are all set. When configured, the settings panel starts collapsed.
  const isConfigured = Boolean(provider && apiKey && model);

  const pdfFileName = analysisData
    ? `AI Report - ${analysisData.name}.pdf`
    : "AI Report.pdf";

  // Capture the on-screen hitting chart (drawn to the #canvas element) as a
  // downscaled JPEG for embedding into the PDF.
  const captureChart = () => {
    const canvas = document.getElementById("canvas");
    return canvas ? canvasToJpeg(canvas) : null;
  };

  // Build a clean, text-based A4 PDF (selectable text + embedded chart) rather
  // than a full-page screenshot of the report.
  const buildPdfDoc = () =>
    buildReportPdf({
      title: t("AI scouting report"),
      subtitle: analysisData ? analysisData.name : selectedTeamLabel,
      markdown: result,
      chart: mode === "player" ? captureChart() : null,
      chartCaption: t("Attack diagram"),
    });

  // Generate and download a proper A4 PDF document.
  const downloadPdf = () => {
    if (!result) {
      return;
    }
    setGeneratingPdf(true);
    try {
      buildPdfDoc().save(pdfFileName);
    } catch (e) {
      console.log(e);
      toast.error(t("Failed to generate PDF."));
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Save the generated report as a compressed PDF against the scouted player so
  // it appears on the player's profile in the Players page. Returns the saved
  // report object (or null on failure) so callers can chain a Share action.
  const buildAndSaveReport = async () => {
    if (!analysisData || !result) {
      return null;
    }
    if (!analysisData.firstName && !analysisData.lastName) {
      toast.error(t("This player has no name to save the report against."));
      return null;
    }
    const uid = getAuth().currentUser?.uid;
    if (!uid) {
      toast.error(t("Please sign in to save reports."));
      return null;
    }
    const pdfBase64 = pdfToBase64(buildPdfDoc());
    // Firestore documents are limited to ~1MB; guard against oversized PDFs.
    if (pdfBase64.length > 1000000) {
      toast.error(
        t("The report is too large to save. Try a single match or fewer sets.")
      );
      return null;
    }
    const refId = playerDocId(
      uid,
      analysisData.firstName,
      analysisData.lastName
    );
    return await saveReportPdf("player", refId, {
      title: `${t("AI scouting report")} \u2013 ${analysisData.name}`,
      pdfBase64,
      sizeBytes: Math.round(pdfBase64.length * 0.75),
    });
  };

  const savePlayerReport = async () => {
    setSavingReport(true);
    try {
      const saved = await buildAndSaveReport();
      if (saved) {
        toast.success(t("Report saved to the player's profile."));
      } else if (analysisData && result) {
        toast.error(t("Could not save the report."));
      }
    } catch (e) {
      console.log(e);
      toast.error(t("Could not save the report."));
    } finally {
      setSavingReport(false);
    }
  };

  // Save the report then immediately open the Share dialog, the same way
  // matches/playlists/live AI Reports are shared.
  const saveAndShareReport = async () => {
    setSavingReport(true);
    try {
      const saved = await buildAndSaveReport();
      if (!saved) {
        if (analysisData && result) {
          toast.error(t("Could not save the report."));
        }
        return;
      }
      setShareNonce((n) => n + 1);
      setSharingReport(saved);
    } catch (e) {
      console.log(e);
      toast.error(t("Could not save the report."));
    } finally {
      setSavingReport(false);
    }
  };

  const doDoShareReport = async (share) => {
    const report = sharingReport;
    setSharingReport(null);
    if (!share || !report) return;
    const ok = await shareReport(report, share);
    if (!ok) {
      toast.error(t("Could not share the report. Please try again."));
      return;
    }
    if (share.notifyByEmail && share.shareStatus === 2) {
      sendShareNotification({
        to: share.shareUsers,
        itemType: "report",
        itemTitle: report.title,
        sharedByEmail: getAuth().currentUser?.email,
      });
    }
    toast.success(t("Report saved and shared."));
  };

  useEffect(() => {
    const storedProvider = localStorage.getItem(kAIProviderStorage);
    const activeProvider =
      storedProvider && kProviders[storedProvider] ? storedProvider : "openai";
    setProvider(activeProvider);

    const storedKey = localStorage.getItem(keyStorageFor(activeProvider));
    setApiKey(storedKey || "");

    const storedModel = localStorage.getItem(modelStorageFor(activeProvider));
    setModel(storedModel || kProviders[activeProvider].models[0]);

    // Collapse the settings panel on load if everything is already configured.
    setShowSettings(!(storedKey && (storedModel || kProviders[activeProvider].models[0])));
  }, []);

  useEffect(() => {
    // Always start with a fresh report; never restore a previous session's
    // report for the selected player.
    setError(null);
    setResult("");
  }, [selectedPlayerGuid]);

  const saveProvider = (value) => {
    setProvider(value);
    localStorage.setItem(kAIProviderStorage, value);
    setApiKey(localStorage.getItem(keyStorageFor(value)) || "");
    const storedModel = localStorage.getItem(modelStorageFor(value));
    setModel(storedModel || kProviders[value].models[0]);
  };

  const saveKey = (value) => {
    setApiKey(value);
    localStorage.setItem(keyStorageFor(provider), value);
  };

  const saveModel = (value) => {
    setModel(value);
    localStorage.setItem(modelStorageFor(provider), value);
  };

  const callOpenAI = (messages) =>
    callAI({ provider, apiKey, model, messages });

  const handleDownloadCSV = () => {
    const { csv, rowCount } = buildMatchEventsCSV(matches, selectedGame);
    if (!csv || rowCount === 0) {
      toast.error(t("No coded events to export."));
      return;
    }
    downloadCSV(csv, matchCSVFileName(matches));
  };

  const analyzeTeam = async () => {
    if (!apiKey) {
      toast.error(t("Please enter your API key."));
      return;
    }
    const prompt = buildDeepAnalysisPrompt(matches, selectedGame, selectedTeam);
    if (!prompt) {
      toast.error(t("No coded events to analyse."));
      return;
    }
    setLoading(true);
    setError(null);
    setResult("");
    try {
      const messages = [
        {
          role: "system",
          content:
            "You are an expert volleyball performance analyst and scout. You produce clear, practical, evidence-based analysis from ball-by-ball event data. " +
            kVolleyballScoringPrimer,
        },
        { role: "user", content: prompt },
      ];
      if (language && language !== "English") {
        messages.push({
          role: "system",
          content: `Write your entire response in ${language}.`,
        });
      }
      const content = await callOpenAI(messages);
      setResult(content || "No response was returned.");
    } catch (e) {
      setError(e.message || "Failed to analyse team.");
    } finally {
      setLoading(false);
    }
  };

  const analyze = async () => {
    if (mode === "team") {
      return analyzeTeam();
    }
    if (!apiKey) {
      toast.error(t("Please enter your API key."));
      return;
    }
    if (!analysisData) {
      toast.error(t("Select a player with recorded spikes first."));
      return;
    }
    setLoading(true);
    setError(null);
    setResult("");
    try {
      const playerTeamLabel = isPlayerTeamA ? "Team A" : "Team B";
      const opponentTeamLabel = isPlayerTeamA ? "Team B" : "Team A";
      const prompt = buildPrompt(analysisData, playerTeamLabel, opponentTeamLabel);
      const messages = [
        {
          role: "system",
          content:
            "You are an expert volleyball performance analyst and coach. You produce clear, practical scouting reports. " +
            kVolleyballScoringPrimer,
        },
        { role: "user", content: prompt },
      ];
      if (language && language !== "English") {
        messages.push({
          role: "system",
          content: `Write your entire response in ${language}.`,
        });
      }
      const content = await callOpenAI(messages);
      const finalText = content || "No response was returned.";
      setResult(finalText);
    } catch (e) {
      setError(e.message || "Failed to analyse player.");
    } finally {
      setLoading(false);
    }
  };

  const translate = async (targetLang) => {
    if (!apiKey) {
      toast.error(t("Please enter your API key."));
      return;
    }
    if (!result || !targetLang) {
      return;
    }
    setTranslating(true);
    setError(null);
    try {
      const content = await callOpenAI([
        {
          role: "system",
          content: `You are a professional translator. Translate the following volleyball scouting report into ${targetLang}. Preserve the structure, section headings, and bullet points. Only output the translation.`,
        },
        { role: "user", content: result },
      ]);
      if (content) {
        setResult(content);
        setTranslateTo(targetLang);
        setTranslateNonce((n) => n + 1);
      }
    } catch (e) {
      setError(e.message || "Failed to translate report.");
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-3 text-sm">
      <div className="rounded-md border border-base-300 bg-base-100">
        <button
          type="button"
          className="flex w-full items-center justify-between px-3 py-2 text-xs font-semibold text-base-content/70"
          onClick={() => setShowSettings((s) => !s)}
        >
          <span>
            {t("AI settings")}
            {!showSettings && (
              <span className="ml-2 font-normal text-base-content/50">
                {providerConfig(provider).label} · {model}
              </span>
            )}
          </span>
          <span className="flex items-center gap-1">
            {!isConfigured && (
              <span className="badge badge-warning badge-xs">{t("Setup needed")}</span>
            )}
            <span>{showSettings ? "▲" : "▼"}</span>
          </span>
        </button>
        {showSettings && (
          <div className="flex flex-col gap-3 border-t border-base-300 p-3">
            <div>
              <label className="block text-xs font-medium text-base-content/70 mb-1">
                {t("AI provider")}
              </label>
              <select
                value={provider}
                onChange={(e) => saveProvider(e.target.value)}
                className="select select-sm select-bordered w-full"
              >
                {Object.keys(kProviders).map((id) => (
                  <option key={id} value={id}>
                    {kProviders[id].label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-base-content/70 mb-1">
                {providerConfig(provider).keyLabel}
              </label>
              <div className="flex gap-1">
                <input
                  type={showKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => saveKey(e.target.value)}
                  placeholder={providerConfig(provider).keyPlaceholder}
                  autoComplete="off"
                  className="input input-sm input-bordered w-full"
                />
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setShowKey((s) => !s)}
                >
                  {showKey ? t("Hide") : t("Show")}
                </button>
              </div>
              <p className="text-[10px] text-base-content/50 mt-1">
                {t(
                  "Your key is stored only in this browser and sent directly to the selected provider."
                )}
              </p>
            </div>

            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-base-content/70 mb-1">
                  {t("Model")}
                </label>
                <select
                  value={model}
                  onChange={(e) => saveModel(e.target.value)}
                  className="select select-sm select-bordered w-full"
                >
                  {providerConfig(provider).models.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      {canTeamAnalyse && (
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="join">
            <button
              type="button"
              className={`btn btn-xs join-item ${
                mode === "player" ? "btn-primary" : "btn-outline"
              }`}
              onClick={() => setMode("player")}
            >
              {t("Player scouting")}
            </button>
            <button
              type="button"
              className={`btn btn-xs join-item ${
                mode === "team" ? "btn-primary" : "btn-outline"
              }`}
              onClick={() => setMode("team")}
            >
              {t("Team deep analysis")}
            </button>
          </div>
          {mode === "team" && (
            <button
              type="button"
              className="btn btn-xs btn-outline"
              onClick={handleDownloadCSV}
            >
              {t("Download CSV")}
            </button>
          )}
        </div>
      )}

      {mode === "team" ? (
        <div className="rounded-sm bg-base-100 p-2 text-xs">
          <p>
            {t(
              "Deep analysis of {team} from ball-by-ball event data, covering serving, reception, setting, attack, blocking, defence, rotations and opponent tendencies. Best used with the Anthropic (Claude) provider."
            ).replace("{team}", selectedTeamLabel)}
          </p>
        </div>
      ) : (
        <div>
          <label className="block text-xs font-medium text-base-content/70 mb-1">
            {t("Player")}
          </label>
          <select
            value={selectedPlayerGuid || ""}
            onChange={(e) => onPlayerSelected(e.target.value || null)}
            className="select select-sm select-bordered w-full"
          >
            <option value="">{t("Select a player…")}</option>
            {players.map((p) => (
              <option key={p.guid} value={p.guid}>
                {p.name} ({p.count})
              </option>
            ))}
          </select>
          {players.length === 0 && (
            <p className="text-[10px] text-base-content/50 mt-1">
              {t("No players with recorded spikes in the selected matches.")}
            </p>
          )}
        </div>
      )}

      {mode === "player" && analysisData && (
        <div className="rounded-sm bg-base-100 p-2 text-xs">
          <p className="font-semibold">{analysisData.name}</p>
          <p>
            {analysisData.totalSpikes} attacks · {analysisData.kills} kills ·{" "}
            {analysisData.killPct.toFixed(0)}% kill ·{" "}
            {analysisData.efficiency.toFixed(0)}% eff
          </p>
        </div>
      )}

      <button
        type="button"
        className="btn btn-sm btn-primary"
        onClick={analyze}
        disabled={loading || (mode === "player" && !selectedPlayerGuid)}
      >
        {loading ? (
          <span className="loading loading-spinner loading-xs"></span>
        ) : mode === "team" ? (
          t("Analyse team with AI")
        ) : (
          t("Analyse with AI")
        )}
      </button>

      {error && (
        <div className="alert alert-error text-xs p-2">
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-end gap-1">
            <div className="flex items-center gap-1">
              {translating && (
                <span className="loading loading-spinner loading-xs"></span>
              )}
              <select
                className="select select-xs select-bordered"
                value=""
                disabled={translating}
                onChange={(e) => translate(e.target.value)}
                aria-label="Translate report"
              >
                <option value="" disabled>
                  {t("Translate to…")}
                </option>
                {kLanguages.map((lang) => (
                  <option key={lang} value={lang}>
                    {kLangNameToFlag[lang] ? `${kLangNameToFlag[lang]} ` : ""}
                    {t(lang)}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="btn btn-xs"
              onClick={downloadPdf}
              disabled={generatingPdf}
            >
              {generatingPdf ? (
                <span className="loading loading-spinner loading-xs"></span>
              ) : (
                t("Download PDF")
              )}
            </button>
            {mode === "player" && analysisData && (
              <button
                type="button"
                className="btn btn-xs btn-primary"
                onClick={savePlayerReport}
                disabled={savingReport}
                title={t("Save this report to the player's profile")}
              >
                {savingReport ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  t("Save to player")
                )}
              </button>
            )}
            {mode === "player" && analysisData && (
              <button
                type="button"
                className="btn btn-xs"
                onClick={saveAndShareReport}
                disabled={savingReport}
                title={t("Save this report so you can share it with other users")}
              >
                {savingReport ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  t("Save & Share")
                )}
              </button>
            )}
          </div>
          <div
            ref={reportRef}
            className="rounded-sm bg-base-100 p-3 text-xs leading-relaxed print:bg-white print:text-black"
          >
            <div className="hidden print:block mb-3 border-b pb-2">
              <h1 className="text-lg font-bold">{t("AI scouting report")}</h1>
              {analysisData && (
                <p className="text-sm font-semibold">{analysisData.name}</p>
              )}
            </div>
            <div className="print:flex print:gap-4 print:items-start">
              <div className="print:w-1/2 [&_h1]:break-inside-avoid [&_h2]:break-inside-avoid [&_h3]:break-inside-avoid [&_li]:break-inside-avoid [&_table]:break-inside-avoid">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ node, ...props }) => (
                      <h1 className="text-base font-bold mt-3 mb-1" {...props} />
                    ),
                    h2: ({ node, ...props }) => (
                      <h2 className="text-sm font-bold mt-3 mb-1" {...props} />
                    ),
                    h3: ({ node, ...props }) => (
                      <h3
                        className="text-sm font-semibold mt-2 mb-1"
                        {...props}
                      />
                    ),
                    p: ({ node, ...props }) => (
                      <p className="mb-2" {...props} />
                    ),
                    ul: ({ node, ...props }) => (
                      <ul
                        className="list-disc ml-4 mb-2 space-y-0.5"
                        {...props}
                      />
                    ),
                    ol: ({ node, ...props }) => (
                      <ol
                        className="list-decimal ml-4 mb-2 space-y-0.5"
                        {...props}
                      />
                    ),
                    strong: ({ node, ...props }) => (
                      <strong className="font-bold" {...props} />
                    ),
                    em: ({ node, ...props }) => (
                      <em className="italic" {...props} />
                    ),
                    table: ({ node, ...props }) => (
                      <table
                        className="table-auto border-collapse my-2 text-[11px]"
                        {...props}
                      />
                    ),
                    th: ({ node, ...props }) => (
                      <th className="border px-2 py-1 text-left" {...props} />
                    ),
                    td: ({ node, ...props }) => (
                      <td className="border px-2 py-1" {...props} />
                    ),
                  }}
                >
                  {result}
                </ReactMarkdown>
              </div>
            </div>
            <FollowUpQuestions
              // Remount per player: without this, switching players left the
              // previous player's answered Q&A (and its "Saved questions"
              // list, via a shared storageKey) visible under the new report.
              key={selectedPlayerGuid}
              report={result}
              callModel={callOpenAI}
              systemPrompt={
                "You are an expert volleyball performance analyst and coach. You produce clear, practical scouting reports. " +
                kVolleyballScoringPrimer
              }
              language={language}
              storageKey={`VBLiveFollowUpQuestions_aicoach_${selectedPlayerGuid || "none"}`}
              disabled={!apiKey}
              translateTo={translateTo}
              translateNonce={translateNonce}
            />
          </div>
        </div>
      )}

      {sharingReport && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="mb-2 text-lg font-bold">
              {t("Share")} — {sharingReport.title}
            </h3>
            <Share
              shareStatus={sharingReport.shareStatus}
              shareUsers={sharingReport.shareUsers}
              currentTime={shareNonce}
              onShare={doDoShareReport}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AICoachPanel;
